// @flow

import * as React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { isBefore } from "date-fns";
import type { State } from "@store/reducers";
import type { NewReferralsTrackerItem } from "@modules/reporting/reducer/referral-tracker/types";
import type { PaginationExtendedType } from "@types";
import { formatDateTime } from "@transformers/date";
import actionCreators from "@modules/reporting/actionCreator";
import {
  selectNewReferrals,
  selectNewReferralsPagination,
  selectNewReferralsStats,
} from "@modules/reporting/selectors/referral-tracker";
import { GSGrid, Pagination, StatTileGroup } from "@components";
import { SUPPLIER_REFERRAL_REPORT_TYPE } from "@modules/reporting/constants/reports";
import "../styles.scss";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  stats: Object,
  newReferrals: NewReferralsTrackerItem[],
  pagination: PaginationExtendedType,
}>;

type PropsFromDispatch = $Exact<{
  getNewReferralsList: typeof actionCreators.GetSupplierNewReferrals,
  getSupplierReferralDetails: typeof actionCreators.GetSupplierReferralDetails,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...OwnProps,
}>;

export class NewReferralsSection extends React.Component<Props> {
  UNSAFE_componentWillMount() {
    this.props.getNewReferralsList({ clientId: this.props.clientId, page: 1 });
  }

  handlePaginationChange = (page: number) => {
    this.props.getNewReferralsList({
      clientId: this.props.clientId,
      page,
    });
  };

  get statsItems() {
    return [
      {
        title: this.props.t("NEW_REFERRALS"),
        value: this.props.stats.newReferrals,
        className: "bg-light-grey",
      },
      {
        title: this.props.t("EMPLOYEE_LED_REFERRALS"),
        value: this.props.stats.employeeLedReferrals,
      },
      {
        title: this.props.t("MANAGER_LED_REFERRALS"),
        value: this.props.stats.managerLedReferrals,
      },
      {
        title: this.props.t("MANUAL_REFERRALS"),
        value: this.props.stats.manualReferrals,
      },
      {
        title: this.props.t("OVERDUE_REFERRALS"),
        value: this.props.stats.overdueReferrals,
        className: "overdue",
      },
    ];
  }

  get columnsConfig() {
    return [
      {
        name: this.props.t("REFERRAL_ID"),
        width: 90,
        frozen: "left",
        key: "referralId",
      },
      {
        name: this.props.t("EMPLOYEE_REF"),
        align: "left",
        key: "employeeRef",
        tooltipField: "employeeRef",
      },
      {
        name: this.props.t("FIRST_NAME"),
        width: 150,
        key: "firstName",
        align: "left",
      },
      {
        name: this.props.t("LAST_NAME"),
        width: 150,
        key: "lastName",
        align: "left",
      },
      {
        name: this.props.t("DEPARTMENT"),
        width: 212,
        align: "left",
        key: "departmentNames",
        renderCell: ({ row }: *) => GSGrid.render.ellipsis(row.departmentNames),
      },
      {
        name: this.props.t("REFERRAL_TYPE"),
        width: 150,
        renderCell: ({ row }: *) => {
          return row.referralType ? this.props.t(row.referralType) : "";
        },
      },
      {
        name: this.props.t("DATE_REFERRED"),
        width: 150,
        renderCell: ({ row }: *) => {
          return row.dateReferred ? formatDateTime(row.dateReferred) : "";
        },
      },
      {
        name: this.props.t("REASON_FOR_REFERRAL"),
        width: 212,
        key: "reason",
        tooltipField: "reason",
        align: "left",
        renderCell: ({ row }: *) => GSGrid.render.ellipsis(row.reason),
      },
      {
        name: this.props.t("TARGET_DATE"),
        key: "targetDate",
        typeConfig: "dateTime",
        renderCell: ({ row }: *) => {
          const now = new Date();
          const val = row.targetDate ? row.targetDate : "";
          const date = formatDateTime(val);

          return (
            <div
              className={isBefore(new Date(val), now) ? "text-pumpkin-orange font-weight-bold" : ""}
            >
              {date}
            </div>
          );
        },
      },
      {
        name: this.props.t("DETAILS"),
        typeConfig: "action",
        onCellClick: (row: Object) => this.onDetailsCellClick(row),
        renderCell: ({ row }: *) => GSGrid.render.materialIcon("search"),
      },
    ];
  }

  onDetailsCellClick = (row: *) => {
    this.props.getSupplierReferralDetails({
      supplierReferralId: row.referralId,
      clientId: this.props.clientId,
      referralType: SUPPLIER_REFERRAL_REPORT_TYPE.NEW_REFERRALS,
    });
  };

  render() {
    const { newReferrals, pagination } = this.props;

    return (
      <React.Fragment>
        <StatTileGroup data={this.statsItems} />

        {!!newReferrals.length && (
          <div className="w-100 d-flex mb-4 flex-column">
            <GSGrid columns={this.columnsConfig} rows={newReferrals} />
            <Pagination
              className="my-3 d-flex flex-fill justify-content-center"
              currentPage={pagination.pageNumber}
              totalPages={pagination.totalPages}
              onChange={this.handlePaginationChange}
            />
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    stats: selectNewReferralsStats(state),
    newReferrals: selectNewReferrals(state),
    pagination: selectNewReferralsPagination(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getNewReferralsList: actionCreators.GetSupplierNewReferrals,
  getSupplierReferralDetails: actionCreators.GetSupplierReferralDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(NewReferralsSection));
